import type { PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../../store/rootStore";
import type { TreeItemDataOut } from "./services/dashboardTypes";

export interface LayoutState {
  selectedSegment: string;
  treeDataWithPaths: TreeItemDataOut[];
  selectedTreeItem: TreeItemDataOut | null;
  expandedItemsByDepth: { [key: number]: string };
  sidebarSearch: string;
  mobileSidebarOpen: boolean;
  desktopSidebarOpen: boolean;
  theme: string;
}
const initialTheme = localStorage.getItem("theme") || "EnergyGainTheme";

const initialState: LayoutState = {
  selectedSegment: "",
  treeDataWithPaths: [],
  expandedItemsByDepth: {},
  sidebarSearch: "",
  selectedTreeItem: null,
  mobileSidebarOpen: false,
  desktopSidebarOpen: true,
  theme: initialTheme,
};

export const layoutSlice = createSlice({
  name: "layoutSlice",
  initialState,
  reducers: {
    setTreeDataWithPaths: (state, action: PayloadAction<TreeItemDataOut[]>) => {
      state.treeDataWithPaths = action.payload;
    },
    setSelectedTreeItemByResourceId: (state, action: PayloadAction<string | null>) => {
      const newItem = action.payload
        ? findByResourceId(state.treeDataWithPaths, action.payload)
        : state.selectedTreeItem;
      if (newItem) {
        state.selectedTreeItem = newItem;
      }
    },
    setExpandedItemByDepth: (state, action: PayloadAction<{ depth: number; itemId: string }>) => {
      state.expandedItemsByDepth[action.payload.depth] = action.payload.itemId;
    },
    removeExpandedItemByDepth: (state, action: PayloadAction<number>) => {
      delete state.expandedItemsByDepth[action.payload];
    },
    setSearch: (state, action: PayloadAction<string>) => {
      state.sidebarSearch = action.payload;
    },
    setMobileSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.mobileSidebarOpen = action.payload;
    },
    setDesktopSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.desktopSidebarOpen = action.payload;
    },
    toggleTheme: (state) => {
      state.theme = state.theme === "EnergyGainTheme" ? "EnergyGainDarkTheme" : "EnergyGainTheme";
      localStorage.setItem("theme", state.theme);
    },
  },
});

export const selectResourceById = createSelector(
  [(state: RootState) => state.layout.treeDataWithPaths, (_, id: string) => id],
  (treeDataWithPaths, id) => {
    return findByResourceId(treeDataWithPaths, id);
  },
);

function findByResourceId(tree: TreeItemDataOut[], resourceId: string | null): TreeItemDataOut | null {
  if (!resourceId) {
    return null;
  }
  for (const node of tree) {
    if (node.id === resourceId) {
      return node;
    }
    if (node.children?.length) {
      const result = findByResourceId(node.children, resourceId);
      if (result) {
        return result;
      }
    }
  }
  return null;
}

export const {
  setTreeDataWithPaths,
  setExpandedItemByDepth,
  removeExpandedItemByDepth,
  setSelectedTreeItemByResourceId,
  setSearch,
  setMobileSidebarOpen,
  setDesktopSidebarOpen,
  toggleTheme,
} = layoutSlice.actions;

export const selectSelectedSegment = (state: RootState) => state.layout.selectedSegment;
export const selectTreeDataWithPaths = (state: RootState) => state.layout.treeDataWithPaths;
export const selectExpandedItemsByDepth = (state: RootState) => state.layout.expandedItemsByDepth;
export const selectSelectedTreeItem = (state: RootState) => state.layout.selectedTreeItem;
export const selectSearch = (state: RootState) => state.layout.sidebarSearch;
export const selectMobileSidebarOpen = (state: RootState) => state.layout.mobileSidebarOpen;
export const selectDesktopSidebarOpen = (state: RootState) => state.layout.desktopSidebarOpen;
export const selectTheme = (state: RootState) => state.layout.theme;

export const layoutReducer = layoutSlice.reducer;
